import * as joda from "@js-joda/core";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollmentid,
  enrollmentrequest_origin,
  enrollmentrequest_status,
  formula_color,
  formula_type,
  invoicelinetype_serialized,
  lesson_creation,
  lesson_location,
  lesson_removed_reason,
  lesson_status,
  mollie_payment_status,
  month,
  payment_method,
  phone_type,
  single_due_date_calculation,
  weekday,
} from "../sql/classy/types";
import { capitalize } from "./lib/capitalize";
import { parseIntSafe } from "./lib/parseintsafe";

export function parseWeekday(wd: weekday | string): joda.DayOfWeek {
  switch (wd.toLowerCase()) {
    case "monday":
      return joda.DayOfWeek.MONDAY;
    case "tuesday":
      return joda.DayOfWeek.TUESDAY;
    case "wednesday":
      return joda.DayOfWeek.WEDNESDAY;
    case "thursday":
      return joda.DayOfWeek.THURSDAY;
    case "friday":
      return joda.DayOfWeek.FRIDAY;
    case "saturday":
      return joda.DayOfWeek.SATURDAY;
    case "sunday":
      return joda.DayOfWeek.SUNDAY;
    default:
      throw new Error(`Unknown day of week ${wd}`);
  }
}

export function serializeWeekday(dow: joda.DayOfWeek): weekday {
  return capitalize(dow.toString().toLowerCase()) as weekday;
}

export type enrollmentRequestStatus =
  | "InvitationER"
  | "InvitationRejectedER"
  | "Reserved"
  | "WaitingForEmailConfirmationER"
  | "WaitingForEmailConfirmationTimedOutER"
  | "WaitingForPaymentER"
  | "WaitingForPaymentAtProviderER"
  | "WaitingForPaymentTimedOutER"
  | "WaitingForPaymentAtProviderTimedOutER"
  | "WaitingForApprovalER"
  | "DisprovedER"
  | "ConvertedToEnrollment";

export function parseEnrollmentRequestStatus(
  s: enrollmentrequest_status,
): enrollmentRequestStatus {
  if (
    s === "InvitationER" ||
    s === "InvitationRejectedER" ||
    s === "Reserved" ||
    s === "WaitingForEmailConfirmationER" ||
    s === "WaitingForEmailConfirmationTimedOutER" ||
    s === "WaitingForPaymentER" ||
    s === "WaitingForPaymentAtProviderER" ||
    s === "WaitingForPaymentTimedOutER" ||
    s === "WaitingForPaymentAtProviderTimedOutER" ||
    s === "WaitingForApprovalER" ||
    s === "DisprovedER"
  ) {
    return s as enrollmentRequestStatus;
  } else if (s.startsWith("ConvertedToEnrollment")) {
    return "ConvertedToEnrollment" as const;
  } else {
    throw new Error("Failed to parse Enrollment Request Status");
  }
}

export function serializeEnrollmentRequestStatus(
  s: enrollmentRequestStatus,
  enrollmentid: enrollmentid | null,
): enrollmentrequest_status {
  if (
    s === "InvitationER" ||
    s === "InvitationRejectedER" ||
    s === "Reserved" ||
    s === "WaitingForEmailConfirmationER" ||
    s === "WaitingForEmailConfirmationTimedOutER" ||
    s === "WaitingForPaymentER" ||
    s === "WaitingForPaymentAtProviderER" ||
    s === "WaitingForPaymentTimedOutER" ||
    s === "WaitingForPaymentAtProviderTimedOutER" ||
    s === "WaitingForApprovalER" ||
    s === "DisprovedER"
  ) {
    return s as enrollmentrequest_status;
  } else if (s === "ConvertedToEnrollment") {
    if (enrollmentid === null) {
      throw new Error(
        `Failed to serialize enrollment request status: no enrollmentid provided `,
      );
    }
    return (s + "/" + enrollmentid.toString()) as enrollmentrequest_status;
  } else {
    return checkAllCasesHandled(s);
  }
}

export function urlifyCalendarDate(d: joda.LocalDate): string {
  return (
    d.year() * 10000 +
    d.month().ordinal() * 100 +
    d.dayOfMonth()
  ).toString();
}

export type formulaType =
  | { tag: "Private" }
  | { tag: "Group"; maxnumberofstudents: number }
  | { tag: "Camp"; maxnumberofstudentscamp: number };

export function parseFormulaType(s: formula_type): formulaType {
  if (s.includes("Private")) {
    return { tag: "Private" };
  } else if (s.includes("Group")) {
    return {
      tag: "Group",
      maxnumberofstudents: parseIntSafe(s.replace("Group/", "")) || 2,
    };
  } else if (s.includes("Camp")) {
    return {
      tag: "Camp",
      maxnumberofstudentscamp: parseIntSafe(s.replace("Camp/", "")) || 2,
    };
  } else {
    throw new Error("Failed to parse formula type");
  }
}

export function serializeFormulaType(ft: formulaType): formula_type {
  if (ft.tag === "Private") {
    return "Private" as formula_type;
  } else if (ft.tag === "Group") {
    return ("Group/" + ft.maxnumberofstudents.toString()) as formula_type;
  } else if (ft.tag === "Camp") {
    return ("Camp/" + ft.maxnumberofstudentscamp.toString()) as formula_type;
  } else {
    return checkAllCasesHandled(ft);
  }
}

export type invoicelinetype = "Normal" | "Discount" | "Optional";

export function parseInvoicelineType(
  s: invoicelinetype_serialized,
): invoicelinetype {
  if (s === "Normal" || s === "Discount" || s === "Optional") {
    return s as invoicelinetype;
  } else {
    throw new Error(`Failed to parse invoicelinetype ${s}`);
  }
}

export function serializeInvoicelineType(
  s: invoicelinetype,
): invoicelinetype_serialized {
  if (s === "Normal" || s === "Discount" || s === "Optional") {
    return s as invoicelinetype_serialized;
  } else {
    return checkAllCasesHandled(s);
  }
}

export type lessonCreation =
  | "InitiallyPlanned"
  | "AddedByMoveDueToTeacherAbsence"
  | "AddedByMoveDueToStudentAbsence"
  | "ManuallyAdded"
  | "ManuallyMoved"
  | "MovedBecauseOfNewHoliday"
  | "MovedBecauseOfRemovalOfHoliday";

export function serializeLessonCreation(lc: lessonCreation): lesson_creation {
  return lc as lesson_creation;
}

export type singleDueDateCalculation =
  | { tag: "RelativeToFirstLesson"; days: number }
  | { tag: "RelativeToFirstDayOfPeriod"; days: number }
  | { tag: "RelativeToCreationDate"; days: number };

export function parseInvoiceDueDateSetting(
  s: single_due_date_calculation | null,
): singleDueDateCalculation {
  if (s === null) {
    return {
      tag: "RelativeToFirstLesson",
      days: 14,
    };
  }
  if (s.startsWith("RelativeToFirstLesson")) {
    return {
      tag: "RelativeToFirstLesson",
      days: parseIntSafe(s.slice("RelativeToFirstLesson/".length)) || 14,
    };
  }
  if (s.startsWith("RelativeToCreationDate")) {
    return {
      tag: "RelativeToCreationDate",
      days: parseIntSafe(s.slice("RelativeToCreationDate/".length)) || 14,
    };
  }
  if (s.startsWith("RelativeToFirstDayOfPeriod")) {
    return {
      tag: "RelativeToFirstDayOfPeriod",
      days: parseIntSafe(s.slice("RelativeToFirstDayOfPeriod/".length)) || 14,
    };
  }
  throw new Error("Failed to parse invoice due date");
}

export function serializeInvoiceDueDateSetting(
  s: singleDueDateCalculation,
): single_due_date_calculation {
  return (s.tag + "/" + s.days.toString()) as single_due_date_calculation;
}

export type paymentMethod =
  | "Cash"
  | "Bancontact"
  | "VISA"
  | "Mastercard"
  | "Wire"
  | "Online"
  | "DirectDebit"
  | "Chargeback"
  | "Refund"
  | "AccountingSoftware"
  | "Giftcard";

export function parsePaymentMethod(p: payment_method): paymentMethod {
  if (
    p === "Cash" ||
    p === "Bancontact" ||
    p === "VISA" ||
    p === "Mastercard" ||
    p === "Wire" ||
    p === "Online" ||
    p === "AccountingSoftware" ||
    p === "DirectDebit" ||
    p === "Chargeback" ||
    p === "Refund" ||
    p === "Giftcard"
  ) {
    return p as paymentMethod;
  } else {
    throw new Error(`Failed to parse payment method ${p}`);
  }
}

export function serializePaymentMethod(p: paymentMethod) {
  return p as payment_method;
}

export type lessonRemovedReason =
  | "MovedDueToTeacherAbsence"
  | "DeletedDueToTeacherAbsence"
  | "DeletedManually"
  | "RemovedBecauseOfNewHoliday"
  | "RemovedBecauseOfRemovalOfHoliday"
  | "StudentLegalAbsence";

export function parseLessonRemovedReason(
  p: lesson_removed_reason,
): lessonRemovedReason {
  if (
    p === "MovedDueToTeacherAbsence" ||
    p === "DeletedDueToTeacherAbsence" ||
    p === "DeletedManually" ||
    p === "RemovedBecauseOfNewHoliday" ||
    p === "RemovedBecauseOfRemovalOfHoliday" ||
    p === "StudentLegalAbsence"
  ) {
    return p as lessonRemovedReason;
  } else {
    throw new Error(`Failed to parse lesson removed reason ${p}`);
  }
}

export function serializeLessonRemovedReason(
  p: lessonRemovedReason,
): lesson_removed_reason {
  return p as lesson_removed_reason;
}

export type phoneType = "Phone" | "CellPhone" | "Mother" | "Father";
export const allPhoneTypes: phoneType[] = [
  "Phone",
  "CellPhone",
  "Mother",
  "Father",
];

export function parsePhoneType(p: phone_type): phoneType {
  if (p === "Phone" || p === "CellPhone" || p === "Mother" || p === "Father") {
    return p as phoneType;
  } else {
    throw new Error(`Failed to parse phone type ${p}`);
  }
}

export function serializePhoneType(p: phoneType): phone_type {
  return p as phone_type;
}

export function showPhoneType(ph: phoneType): string {
  switch (ph) {
    case "Phone":
      return "Telefoon";
    case "CellPhone":
      return "GSM";
    case "Mother":
      return "GSM Moeder";
    case "Father":
      return "GSM Vader";
    default:
      return checkAllCasesHandled(ph);
  }
}

export type lessonStatus = "Open" | "Done" | "StudentAbsent";

export function parseLessonStatus(p: lesson_status): lessonStatus {
  if (p === "Open" || p === "Done" || p === "StudentAbsent") {
    return p as lessonStatus;
  } else {
    throw new Error(`Failed to parse lesson status ${p}`);
  }
}

export function serializeLessonStatus(p: lessonStatus): lesson_status {
  return p as lesson_status;
}

export const allFormulaColors: formulaColor[] = [
  "Pink",
  "Purple",
  "Green",
  "Orange",
  "Red",
];

export type formulaColor = "Pink" | "Purple" | "Green" | "Orange" | "Red";

export function parseFormulaColor(p: formula_color): formulaColor {
  if (
    p === "Pink" ||
    p === "Purple" ||
    p === "Green" ||
    p === "Orange" ||
    p === "Red"
  ) {
    return p as formulaColor;
  } else {
    throw new Error(`Failed to parse formula color ${p}`);
  }
}

export function serializeFormulaColor(p: formulaColor): formula_color {
  return p as formula_color;
}

export type molliePaymentStatus =
  | "Open"
  | "Canceled"
  | "Pending"
  | "Expired"
  | "Failed"
  | "Paid";

export function parseMolliePaymentStatus(
  s: mollie_payment_status,
): molliePaymentStatus {
  if (
    s.toLowerCase() === "open" ||
    s.toLowerCase() === "canceled" ||
    s.toLowerCase() === "pending" ||
    s.toLowerCase() === "expired" ||
    s.toLowerCase() === "failed" ||
    s.toLowerCase() === "paid"
  ) {
    return capitalize(s) as molliePaymentStatus;
  } else {
    throw new Error(`Failed to parse mollie_payment_status ${s}`);
  }
}

export function serializeMolliePaymentStatus(
  s: molliePaymentStatus,
): mollie_payment_status {
  return s as mollie_payment_status;
}

export type enrollmentrequestOrigin = "PublicPlanning" | "Reenrollment";

export function parseEnrollmentrequestOrigin(
  s: enrollmentrequest_origin,
): enrollmentrequestOrigin {
  if (s === "PublicPlanning" || s === "Reenrollment") {
    return s as enrollmentrequestOrigin;
  } else {
    throw new Error(`Failed to parse enrollmentrequest_origin ${s}`);
  }
}

export function serializeEnrollmentrequestOrigin(
  s: enrollmentrequestOrigin,
): enrollmentrequest_origin {
  return s as enrollmentrequest_origin;
}

export const allMonths = [
  "January" as const,
  "February" as const,
  "March" as const,
  "April" as const,
  "May" as const,
  "June" as const,
  "July" as const,
  "August" as const,
  "September" as const,
  "October" as const,
  "November" as const,
  "December" as const,
];
export function parseMonth(m: month): joda.Month {
  return joda.Month.of(allMonths.findIndex((m2) => m === m2) + 1);
}
